import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getCurrentUser } from '../Authentication/useCurrentUser';
import {
  FiGrid, FiUsers, FiBookOpen, FiCalendar, FiMapPin, FiFileText,
  FiDollarSign, FiAward, FiClipboard, FiPackage, FiHome, FiUser,
  FiChevronRight, FiBarChart2, FiBook, FiShoppingBag
} from 'react-icons/fi';
import SUZA from '../assets/SUZA.png';

const sidebarItems = [
  { to: '/dashboard', label: 'Dashboard', icon: FiGrid, roles: ['ADMIN'] },
  { to: '/host-dashboard', label: 'Dashboard', icon: FiGrid, roles: ['HOST'] },
  { to: '/instructor-dashboard', label: 'Dashboard', icon: FiGrid, roles: ['INSTRUCTOR'] },
  { to: '/student-dashboard', label: 'Dashboard', icon: FiGrid, roles: ['STUDENT'] },
  { to: '/intake', label: 'Intake Management', icon: FiCalendar, roles: ['ADMIN'] },
  { to: '/application', label: 'Application', icon: FiFileText, roles: ['ADMIN'] },
  {
    label: 'Host Management', icon: FiHome,
    submenu: [
      { to: '/host', label: 'Hosts', roles: ['ADMIN', 'HOST'] },
      { to: '/gethostbyname', label: 'Search by Name', roles: ['ADMIN', 'HOST'] },
      { to: '/gethostbyaddress', label: 'Search by Address', roles: ['ADMIN', 'HOST'] },
      { to: '/allocation', label: 'Allocate Student', roles: ['ADMIN', 'HOST'] },
      { to: '/facilities', label: 'Facilities', roles: ['HOST', 'ADMIN'] },
    ],
  },
  {
    label: 'Trips Management', icon: FiMapPin,
    submenu: [
      { to: '/trips', label: 'Field Trips', roles: ['ADMIN', 'INSTRUCTOR'] },
      { to: '/tripsdate', label: 'Trips by Date', roles: ['ADMIN', 'INSTRUCTOR', 'STUDENT'] },
      { to: '/trips-of-student', label: 'Student Trips', roles: ['INSTRUCTOR'] },
    ],
  },
  {
    label: 'Claiming', icon: FiDollarSign,
    submenu: [
      { to: '/claim-instructor', label: 'Instructor Claims', roles: ['ADMIN'] },
    ],
  },
  {
    label: 'Resources', icon: FiPackage,
    submenu: [
      { to: '/resources', label: 'Resources', roles: ['ADMIN', 'INSTRUCTOR'] },
      { to: '/purchase', label: 'Purchases', roles: ['ADMIN'] },
      { to: '/resources-purchase', label: 'Buy Resources', roles: ['INSTRUCTOR', 'STUDENT'] },
      { to: '/student-resources', label: 'My Resources', roles: ['STUDENT'] },
    ],
  },
  {
    label: 'Students', icon: FiUsers,
    submenu: [
      { to: '/student', label: 'All Students', roles: ['ADMIN'] },
      { to: '/studenttrip', label: 'Student Trips', roles: ['ADMIN'] },
      { to: '/studentintake', label: 'Student Intake', roles: ['ADMIN', 'INSTUCTOR'] },
      { to: '/studentfees', label: 'Student Fees', roles: ['ADMIN'] },
      { to: '/locationtrip', label: 'Location', roles: ['ADMIN'] },
      { to: '/studenttripbyid', label: 'Trip by Student', roles: ['ADMIN', 'STUDENT'] },
      { to: '/studentallocation', label: 'Student Host', roles: ['ADMIN', 'STUDENT'] },
      { to: '/cityleaningmode', label: 'City Learning', roles: ['ADMIN'] },
      { to: '/enrolment', label: 'Enrolment', roles: ['ADMIN'] },
      { to: '/statustrip', label: 'Trip Attendance', roles: ['ADMIN', 'INSTRUCTOR'] },
      { to: '/instructor-student', label: 'My Students', roles: ['INSTRUCTOR'] },
      { to: '/host-application', label: 'Apply Host', roles: ['STUDENT'] },
    ],
  },
  {
    label: 'Instructors', icon: FiAward,
    submenu: [
      { to: '/instructor', label: 'Instructors', roles: ['ADMIN'] },
      { to: '/instructorcourse', label: 'Instructor Courses', roles: ['ADMIN'] },
      { to: '/claim-form', label: 'Claims', roles: ['ADMIN', 'INSTRUCTOR'] },
    ],
  },
  { to: '/course', label: 'Courses', icon: FiBookOpen, roles: ['ADMIN'] },
  {
    label: 'Evaluation', icon: FiClipboard,
    submenu: [
      { to: '/evaluation', label: 'Evaluations', roles: ['ADMIN'] },
      { to: '/studentevaluation', label: 'My Evaluation', roles: ['STUDENT'] },
      { to: '/instructorevaluation', label: 'Evaluations', roles: ['ADMIN', 'INSTRUCTOR'] },
    ],
  },
  {
    label: 'Exams', icon: FiBook,
    submenu: [
      { to: '/allasigment', label: 'Assignments', roles: ['ADMIN', 'INSTRUCTOR'] },
      { to: '/quiz', label: 'Student Assignment', roles: ['ADMIN', 'INSTRUCTOR', 'STUDENT'] },
      { to: '/result', label: 'Results', roles: ['ADMIN', 'INSTRUCTOR'] },
      { to: '/student-results', label: 'My Results', roles: ['STUDENT'] },
    ],
  },
  {
    label: 'Payments', icon: FiShoppingBag,
    submenu: [
      { to: '/payment', label: 'Payments', roles: ['ADMIN'] },
      { to: '/findPayment', label: 'By Invoice', roles: ['ADMIN'] },
      { to: '/payment-by-date', label: 'By Date', roles: ['ADMIN'] },
      { to: '/payment-by-type', label: 'By Type', roles: ['ADMIN'] },
      { to: '/payment-host', label: 'Host Payments', roles: ['HOST', 'ADMIN'] },
    ],
  },
  { to: '/user-list', label: 'User Management', icon: FiBarChart2, roles: ['ADMIN'] },
  { to: '/user-profile', label: 'My Profile', icon: FiUser, roles: ['STUDENT', 'ADMIN', 'INSTRUCTOR', 'HOST'] },
];

const AppSidebar = ({ isCollapsed }) => {
  const location = useLocation();
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [filteredItems, setFilteredItems] = useState([]);

  const toggle = (i) => setOpenSubMenu(openSubMenu === i ? null : i);

  useEffect(() => {
    try {
      const user = getCurrentUser();
      if (!user) return;

      let items;
      if (user.status === 'REGISTRATION' || user.status === 'INACTIVE') {
        items = [
          { to: '/student-registration-dashboard', label: 'Dashboard', icon: FiGrid, roles: ['STUDENT'] },
          { to: '/student-registration', label: 'Personal Info', icon: FiUser, roles: ['STUDENT'] },
          { to: '/registration-payment', label: 'Payment', icon: FiDollarSign, roles: ['STUDENT'] },
          { to: '/apply-course', label: 'Course Selection', icon: FiBookOpen, roles: ['STUDENT'] },
          { to: '/application-submission', label: 'Submit Application', icon: FiFileText, roles: ['STUDENT'] },
          { to: '/user-profile', label: 'My Profile', icon: FiUser, roles: ['STUDENT'] },
        ];
      } else {
        items = sidebarItems.map(item => {
          if (item.submenu) {
            const sub = item.submenu.filter(s => s.roles.includes(user.role));
            return sub.length ? { ...item, submenu: sub } : null;
          }
          return item.roles.includes(user.role) ? item : null;
        }).filter(Boolean);
      }
      setFilteredItems(items);
    } catch (_) { }
  }, []);

  return (
    <div style={{ background: 'linear-gradient(180deg,#0f172a 0%,#1e293b 100%)', minHeight: '100vh' }}>
      <aside id="sidebar" className={isCollapsed ? 'collapsed' : ''}>
        <div className="h-100">
          {/* Logo */}
          <div className="sidebar-logo">
            <div className="sidebar-logo-icon">
              <img src={SUZA} alt="SUZA" style={{ width: 22, height: 22, objectFit: 'contain' }} />
            </div>
            <Link to="/" style={{ lineHeight: 1.2 }}>
              <span style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 700, color: '#f8fafc' }}>Swahili For</span>
              <span style={{ display: 'block', fontSize: '0.75rem', fontWeight: 400, color: '#64748b' }}>Foreigners System</span>
            </Link>
          </div>

          {/* Nav */}
          <ul className="sidebar-nav">
            <li className="sidebar-header">Navigation</li>
            {filteredItems.map((item, i) => {
              const Icon = item.icon || FiGrid;
              const isActive = item.to && location.pathname === item.to;
              const isOpen = openSubMenu === i;

              return (
                <li key={i} className="sidebar-item">
                  {item.submenu ? (
                    <>
                      <div
                        className={`sidebar-link${isOpen ? ' active' : ''}`}
                        onClick={() => toggle(i)}
                        style={{ justifyContent: 'flex-start' }}
                      >
                        <Icon size={14} />
                        <span style={{ flex: 1 }}>{item.label}</span>
                        <FiChevronRight size={11} className={`sidebar-chevron${isOpen ? ' open' : ''}`} />
                      </div>
                      {isOpen && (
                        <ul className="submenu">
                          {item.submenu.map((sub, si) => (
                            <li key={si} className="sidebar-subitem">
                              <Link
                                to={sub.to}
                                className={`sidebar-link${location.pathname === sub.to ? ' active' : ''}`}
                              >
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: location.pathname === sub.to ? '#60a5fa' : '#334155', flexShrink: 0 }} />
                                {sub.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <Link to={item.to} className={`sidebar-link${isActive ? ' active' : ''}`}>
                      <Icon size={14} />
                      {item.label}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </aside>
    </div>
  );
};

export default AppSidebar;
